import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Flex, HStack, IconButton, Input, Popover, PopoverBody, PopoverContent,
  PopoverTrigger, Spinner, Text, useColorModeValue, useToast,
} from "@chakra-ui/react";
import { Check, ChevronDown, Pencil, Plus, Trash2, X } from "lucide-react";

import { PERMISSIONS } from "../config/permissions";
import { usePermissionsContext } from "../data/permissions/permissionContext";
import { getToken } from "../lib/auth";
import {
  createHrFieldOption, deleteHrFieldOption, getHrFieldOptions, updateHrFieldOption,
} from "../lib/api";
import type { FieldOption } from "../types/fieldOption";

export interface ManageableSelectProps {
  fieldKey: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
}

/**
 * Dropdown com opções configuráveis para os campos do processo interno de RH.
 * Select estrito: só escolhe opções cadastradas. Valores antigos não cadastrados
 * são exibidos em só-leitura. Usuários com `rh.field_options.manage` podem
 * cadastrar, editar e remover opções dentro do próprio dropdown.
 */
export const ManageableSelect = ({
  fieldKey, value, onChange, placeholder, isDisabled,
}: ManageableSelectProps) => {
  const { permissions } = usePermissionsContext();
  const canManage = permissions.includes(PERMISSIONS.RH.FIELD_OPTIONS_MANAGE);
  const toast = useToast();
  const token = getToken() ?? "";

  const [options, setOptions] = useState<FieldOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // gestão inline
  const [newText, setNewText] = useState("");
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  const [savingId, setSavingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const triggerBg = useColorModeValue("white", "#0a1628");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const rowHoverBg = useColorModeValue("blue.50", "whiteAlpha.50");
  const muted = useColorModeValue("gray.400", "gray.500");
  const valueColor = useColorModeValue("gray.800", "gray.100");
  const menuShadow = useColorModeValue("0 4px 16px rgba(0,0,0,0.12)", "0 4px 16px rgba(0,0,0,0.5)");
  const deleteBg = useColorModeValue("red.50", "rgba(226,53,63,0.15)");
  const deleteTextColor = useColorModeValue("red.600", "red.300");

  const loadOptions = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      setOptions(await getHrFieldOptions(fieldKey, token));
    } catch {
      /* mantém lista anterior */
    } finally {
      setIsLoading(false);
    }
  }, [fieldKey, token]);

  useEffect(() => { void loadOptions(); }, [loadOptions]);
  useEffect(() => { if (isOpen) void loadOptions(); }, [isOpen, loadOptions]);
  // Mede a largura do trigger para o popover ter o mesmo tamanho do input.
  useEffect(() => {
    if (isOpen && triggerRef.current) setMenuWidth(triggerRef.current.offsetWidth);
  }, [isOpen]);

  const registeredValues = new Set(options.map((o) => o.value));
  const valueIsRegistered = !value || registeredValues.has(value);

  const handleSelect = (v: string) => {
    onChange(v);
    setIsOpen(false);
  };

  const handleAdd = async () => {
    const v = newText.trim();
    if (!v) return;
    setAdding(true);
    try {
      const created = await createHrFieldOption(fieldKey, v, token);
      await loadOptions();
      onChange(created.value);
      setNewText("");
      setIsAddingNew(false);
      toast({ title: "Opção adicionada", status: "success", duration: 2500, isClosable: true });
    } catch (e) {
      toast({ title: (e as Error).message || "Erro ao adicionar opção", status: "error", duration: 4000, isClosable: true });
    } finally {
      setAdding(false);
    }
  };

  const cancelAdd = () => {
    setIsAddingNew(false);
    setNewText("");
  };

  const startEdit = (opt: FieldOption) => {
    setEditingId(opt.id);
    setEditText(opt.value);
    setConfirmDeleteId(null);
  };

  const handleSaveEdit = async (id: number) => {
    const v = editText.trim();
    if (!v) return;
    setSavingId(id);
    try {
      const updated = await updateHrFieldOption(id, v, token);
      await loadOptions();
      // se o valor editado era o selecionado, atualiza o draft p/ manter consistência
      if (value === options.find((o) => o.id === id)?.value) onChange(updated.value);
      setEditingId(null);
      toast({ title: "Opção atualizada", status: "success", duration: 2500, isClosable: true });
    } catch (e) {
      toast({ title: (e as Error).message || "Erro ao atualizar opção", status: "error", duration: 4000, isClosable: true });
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    setSavingId(id);
    try {
      await deleteHrFieldOption(id, token);
      await loadOptions();
      setConfirmDeleteId(null);
      toast({ title: "Opção removida", status: "success", duration: 2500, isClosable: true });
    } catch (e) {
      toast({ title: (e as Error).message || "Erro ao remover opção", status: "error", duration: 4000, isClosable: true });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Popover isOpen={isOpen} onClose={() => setIsOpen(false)} placement="bottom-start" isLazy>
      <PopoverTrigger>
        <Flex
          ref={triggerRef}
          as="button"
          w="100%"
          h="32px"
          px={3}
          align="center"
          justify="space-between"
          bg={triggerBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          fontSize="sm"
          color={value ? valueColor : muted}
          cursor={isDisabled ? "not-allowed" : "pointer"}
          opacity={isDisabled ? 0.6 : 1}
          _hover={isDisabled ? {} : { borderColor: "blue.400" }}
          onClick={() => !isDisabled && setIsOpen((o) => !o)}
          type="button"
        >
          <Text isTruncated textAlign="left" flex={1}>
            {value || placeholder || "Selecione..."}
          </Text>
          <ChevronDown size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
        </Flex>
      </PopoverTrigger>

      <PopoverContent
        w={menuWidth ? `${menuWidth}px` : "100%"}
        minW="200px"
        maxW="90vw"
        p={1}
        bg={triggerBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="md"
        boxShadow={menuShadow}
      >
        <PopoverBody p={0}>
          {/* valor atual não cadastrado */}
          {!valueIsRegistered && (
            <Box px={2} py={1.5} mb={1} borderRadius="md" bg={hoverBg}>
              <Text fontSize="xs" color={muted} fontWeight="medium">
                Valor atual: <Text as="span" color={valueColor}>{value}</Text> (não cadastrado)
              </Text>
            </Box>
          )}

          {isLoading ? (
            <Flex justify="center" py={4}><Spinner size="sm" /></Flex>
          ) : options.length === 0 && valueIsRegistered ? (
            <Text fontSize="sm" color={muted} px={2} py={3} textAlign="center">
              Nenhuma opção cadastrada.
            </Text>
          ) : (
            <Box maxH="240px" overflowY="auto">
              {options.map((opt) => (
                <Box key={opt.id}>
                  {editingId === opt.id ? (
                    <HStack spacing={1} px={1} py={1}>
                      <Input
                        size="xs" value={editText} bg={triggerBg}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void handleSaveEdit(opt.id);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        autoFocus
                      />
                      <IconButton
                        aria-label="Salvar" size="xs" variant="ghost" colorScheme="green"
                        icon={<Check size={14} />} isLoading={savingId === opt.id}
                        onClick={() => void handleSaveEdit(opt.id)}
                      />
                      <IconButton
                        aria-label="Cancelar" size="xs" variant="ghost"
                        icon={<X size={14} />} onClick={() => setEditingId(null)}
                      />
                    </HStack>
                  ) : confirmDeleteId === opt.id ? (
                    <Flex
                      align="center" justify="space-between" px={2} py={1.5} borderRadius="md"
                      bg={deleteBg}
                    >
                      <Text fontSize="xs" color={deleteTextColor}>Remover esta opção?</Text>
                      <HStack spacing={1}>
                        <IconButton
                          aria-label="Confirmar" size="xs" variant="ghost" colorScheme="red"
                          icon={<Check size={14} />} isLoading={savingId === opt.id}
                          onClick={() => void handleDelete(opt.id)}
                        />
                        <IconButton
                          aria-label="Cancelar" size="xs" variant="ghost"
                          icon={<X size={14} />} onClick={() => setConfirmDeleteId(null)}
                        />
                      </HStack>
                    </Flex>
                  ) : (
                    <HStack
                      spacing={1}
                      px={2} py={1.5} borderRadius="md" cursor="pointer"
                      _hover={{ bg: rowHoverBg }}
                      onClick={() => handleSelect(opt.value)}
                      role="option"
                    >
                      <Text fontSize="sm" flex={1} isTruncated>{opt.value}</Text>
                      {canManage && (
                        <HStack spacing={0} onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            aria-label="Editar" size="xs" variant="ghost" color={muted}
                            icon={<Pencil size={12} />} onClick={() => startEdit(opt)}
                          />
                          <IconButton
                            aria-label="Remover" size="xs" variant="ghost" color="red.400"
                            icon={<Trash2 size={12} />} onClick={() => setConfirmDeleteId(opt.id)}
                          />
                        </HStack>
                      )}
                    </HStack>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {canManage && (
            <>
              <Box borderTopWidth="1px" borderColor={borderColor} mt={1} mb={0} />
              {isAddingNew ? (
                <HStack spacing={1} px={1} py={1}>
                  <Input
                    size="xs" placeholder="Nova opção..." value={newText} bg={triggerBg}
                    onChange={(e) => setNewText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void handleAdd();
                      if (e.key === "Escape") cancelAdd();
                    }}
                    autoFocus
                  />
                  <IconButton
                    aria-label="Salvar nova opção" size="xs" variant="ghost" colorScheme="green"
                    icon={<Check size={14} />} isLoading={adding}
                    isDisabled={!newText.trim()}
                    onClick={() => void handleAdd()}
                  />
                  <IconButton
                    aria-label="Cancelar" size="xs" variant="ghost"
                    icon={<X size={14} />} onClick={cancelAdd}
                  />
                </HStack>
              ) : (
                <HStack
                  spacing={2} px={2} py={1.5} borderRadius="md" cursor="pointer"
                  justify="center" color="blue.500"
                  _hover={{ bg: rowHoverBg, color: "blue.600" }}
                  onClick={() => setIsAddingNew(true)}
                >
                  <Plus size={14} style={{ flexShrink: 0 }} />
                  <Text fontSize="sm">Nova Opção</Text>
                </HStack>
              )}
            </>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};